import { useEffect, useState } from "react";
const getInitialVehicleDetails = () => ({
  registrationNumber: "",
  make: "",
  model: "",
  color: "",
  vehicleType: "",
});

const VehicleDetailsModal = ({
  isOpen,
  loading = false,
  onClose,
  onSubmit,
}) => {
  const [vehicleDetails, setVehicleDetails] = useState(
    getInitialVehicleDetails,
  );

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      setVehicleDetails(getInitialVehicleDetails());
      setErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape" && !loading) {
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, loading, onClose]);

  if (!isOpen) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;

    setVehicleDetails((current) => ({
      ...current,
      [name]:
        name === "registrationNumber"
          ? value.toUpperCase()
          : value,
    }));

    if (errors[name]) {
      setErrors((current) => ({
        ...current,
        [name]: "",
      }));
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!vehicleDetails.registrationNumber.trim()) {
      nextErrors.registrationNumber =
        "Registration number is required.";
    }

    if (!vehicleDetails.make.trim()) {
      nextErrors.make = "Vehicle make is required.";
    }

    if (!vehicleDetails.model.trim()) {
      nextErrors.model = "Vehicle model is required.";
    }

    if (!vehicleDetails.color.trim()) {
      nextErrors.color = "Vehicle color is required.";
    }

    if (!vehicleDetails.vehicleType) {
      nextErrors.vehicleType = "Vehicle type is required.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading || !validate()) {
      return;
    }

    const normalizedVehicleDetails = {
      registrationNumber:
        vehicleDetails.registrationNumber
          .trim()
          .toUpperCase(),
      make: vehicleDetails.make.trim(),
      model: vehicleDetails.model.trim(),
      color: vehicleDetails.color.trim(),
      vehicleType: vehicleDetails.vehicleType,
    };

    await onSubmit?.(normalizedVehicleDetails);
  };

  const handleOverlayMouseDown = (event) => {
    if (
      event.target === event.currentTarget &&
      !loading
    ) {
      onClose?.();
    }
  };

  return (
    <div
      className="vehicle-modal-overlay"
      role="presentation"
      onMouseDown={handleOverlayMouseDown}
    >
      <div
        className="vehicle-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vehicle-details-title"
      >
        <div className="vehicle-modal-header">
          <div className="vehicle-modal-title-wrap">
            <span className="vehicle-modal-icon">
              <i className="fa-solid fa-car-side" />
            </span>

            <div>
              <h2 id="vehicle-details-title">
                Vehicle Details
              </h2>

              <p>
                Enter the vehicle you'll be bringing to
                the drive-in theatre.
              </p>
            </div>
          </div>

          <button
            type="button"
            className="vehicle-modal-close"
            disabled={loading}
            onClick={onClose}
            aria-label="Close vehicle details"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <form
          className="vehicle-modal-form"
          onSubmit={handleSubmit}
          noValidate
        >
          <div className="vehicle-form-group">
            <label htmlFor="registrationNumber">
              Registration / License Plate
              <span className="vehicle-required">*</span>
            </label>

            <div
              className={`vehicle-input-wrap ${
                errors.registrationNumber ? "has-error" : ""
              }`}
            >
              <i className="fa-solid fa-id-card" />

              <input
                id="registrationNumber"
                type="text"
                name="registrationNumber"
                value={vehicleDetails.registrationNumber}
                onChange={handleChange}
                placeholder="e.g. AB12 CDE"
                autoComplete="off"
                maxLength={20}
                disabled={loading}
              />
            </div>

            {errors.registrationNumber && (
              <small className="vehicle-field-error">
                {errors.registrationNumber}
              </small>
            )}
          </div>

          <div className="vehicle-form-row">
            <div className="vehicle-form-group">
              <label htmlFor="make">
                Vehicle Make
                <span className="vehicle-required">*</span>
              </label>

              <div
                className={`vehicle-input-wrap ${
                  errors.make ? "has-error" : ""
                }`}
              >
                <i className="fa-solid fa-car" />

                <input
                  id="make"
                  type="text"
                  name="make"
                  value={vehicleDetails.make}
                  onChange={handleChange}
                  placeholder="e.g. BMW"
                  autoComplete="off"
                  maxLength={80}
                  disabled={loading}
                />
              </div>

              {errors.make && (
                <small className="vehicle-field-error">
                  {errors.make}
                </small>
              )}
            </div>

            <div className="vehicle-form-group">
              <label htmlFor="model">
                Vehicle Model
                <span className="vehicle-required">*</span>
              </label>

              <div
                className={`vehicle-input-wrap ${
                  errors.model ? "has-error" : ""
                }`}
              >
                <i className="fa-solid fa-car-rear" />

                <input
                  id="model"
                  type="text"
                  name="model"
                  value={vehicleDetails.model}
                  onChange={handleChange}
                  placeholder="e.g. X5"
                  autoComplete="off"
                  maxLength={80}
                  disabled={loading}
                />
              </div>

              {errors.model && (
                <small className="vehicle-field-error">
                  {errors.model}
                </small>
              )}
            </div>
          </div>

          <div className="vehicle-form-row">
            <div className="vehicle-form-group">
              <label htmlFor="color">
                Vehicle Color
                <span className="vehicle-required">*</span>
              </label>

              <div
                className={`vehicle-input-wrap ${
                  errors.color ? "has-error" : ""
                }`}
              >
                <i className="fa-solid fa-palette" />

                <input
                  id="color"
                  type="text"
                  name="color"
                  value={vehicleDetails.color}
                  onChange={handleChange}
                  placeholder="e.g. Black"
                  autoComplete="off"
                  maxLength={50}
                  disabled={loading}
                />
              </div>

              {errors.color && (
                <small className="vehicle-field-error">
                  {errors.color}
                </small>
              )}
            </div>

            <div className="vehicle-form-group">
              <label htmlFor="vehicleType">
                Vehicle Type
                <span className="vehicle-required">*</span>
              </label>

              <div
                className={`vehicle-input-wrap ${
                  errors.vehicleType ? "has-error" : ""
                }`}
              >
                <i className="fa-solid fa-car-side" />

                <select
                  id="vehicleType"
                  name="vehicleType"
                  value={vehicleDetails.vehicleType}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">
                    Select vehicle type
                  </option>
                  <option value="car">Car</option>
                  <option value="suv">SUV</option>
                  <option value="van">Van</option>
                  <option value="pickup">
                    Pickup Truck
                  </option>
                  <option value="motorcycle">
                    Motorcycle
                  </option>
                  <option value="other">Other</option>
                </select>
              </div>

              {errors.vehicleType && (
                <small className="vehicle-field-error">
                  {errors.vehicleType}
                </small>
              )}
            </div>
          </div>

          <div className="vehicle-modal-note">
            <i className="fa-solid fa-circle-info" />

            <span>
              These details help our drive-in team identify
              your vehicle when you arrive.
            </span>
          </div>

          <div className="vehicle-modal-actions">
            <button
              type="button"
              className="vehicle-cancel-btn"
              disabled={loading}
              onClick={onClose}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="vehicle-continue-btn"
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" />
                  Creating Order...
                </>
              ) : (
                <>
                  Continue to Checkout
                  <i className="fa-solid fa-arrow-right" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VehicleDetailsModal;
